// Regulatory obligation categories
export const OBLIGATION_CATEGORIES = {
  DATA_PRIVACY: 'data_privacy',
  CYBERSECURITY: 'cybersecurity',
  WORKPLACE_SAFETY: 'workplace_safety',
  FINANCIAL_REPORTING: 'financial_reporting',
  ENVIRONMENTAL: 'environmental',
  INDUSTRY_SPECIFIC: 'industry_specific'
};

// Regulatory bodies
export const REGULATORY_BODIES = {
  OAIC: {
    name: 'Office of the Australian Information Commissioner',
    jurisdiction: 'Australia',
    website: 'https://www.oaic.gov.au',
    categories: [OBLIGATION_CATEGORIES.DATA_PRIVACY]
  },
  ACSC: {
    name: 'Australian Cyber Security Centre',
    jurisdiction: 'Australia',
    website: 'https://www.cyber.gov.au',
    categories: [OBLIGATION_CATEGORIES.CYBERSECURITY]
  },
  ASIC: {
    name: 'Australian Securities and Investments Commission',
    jurisdiction: 'Australia',
    website: 'https://www.asic.gov.au',
    categories: [OBLIGATION_CATEGORIES.FINANCIAL_REPORTING]
  },
  APRA: {
    name: 'Australian Prudential Regulation Authority',
    jurisdiction: 'Australia',
    website: 'https://www.apra.gov.au',
    categories: [OBLIGATION_CATEGORIES.FINANCIAL_REPORTING]
  },
  SAFEWORK_NSW: {
    name: 'SafeWork NSW',
    jurisdiction: 'New South Wales',
    website: 'https://www.safework.nsw.gov.au',
    categories: [OBLIGATION_CATEGORIES.WORKPLACE_SAFETY]
  },
  EPA_NSW: {
    name: 'NSW Environment Protection Authority',
    jurisdiction: 'New South Wales',
    website: 'https://www.epa.nsw.gov.au',
    categories: [OBLIGATION_CATEGORIES.ENVIRONMENTAL]
  }
};

// Base obligation structure
export const createObligation = ({
  id,
  title,
  description,
  category,
  regulator,
  deadlineHours,
  notificationMethod,
  requiredActions,
  applicableIndustries,
  legalReference,
  aiPrompt
}) => ({
  id,
  title,
  description,
  category,
  regulator,
  deadlineHours,
  notificationMethod,
  requiredActions,
  applicableIndustries,
  legalReference,
  aiPrompt,
  createdAt: new Date(),
  updatedAt: new Date()
});

// Predefined obligations
export const PREDEFINED_OBLIGATIONS = {
  // Data Privacy
  DATA_BREACH_NOTIFICATION: createObligation({
    id: 'data_breach_notification',
    title: 'Notifiable Data Breach Notification',
    description: 'Obligation to notify affected individuals and the OAIC of eligible data breaches',
    category: OBLIGATION_CATEGORIES.DATA_PRIVACY,
    regulator: REGULATORY_BODIES.OAIC,
    deadlineHours: 30,
    notificationMethod: 'Email',
    requiredActions: [
      'Assess if the breach is likely to result in serious harm',
      'Prepare notification statement',
      'Notify affected individuals',
      'Submit notification to OAIC'
    ],
    applicableIndustries: ['all'],
    legalReference: 'Privacy Act 1988 (Cth) - Notifiable Data Breaches scheme',
    aiPrompt: `You are a data breach notification assistant. Your task is to help prepare a notification for a data breach incident.
    Consider the following:
    1. The nature of the breach
    2. The types of personal information involved
    3. The number of individuals affected
    4. The potential harm to affected individuals
    5. The steps taken to contain the breach
    6. The steps taken to mitigate harm
    7. The steps taken to prevent future breaches
    
    Draft a notification that:
    1. Clearly explains what happened
    2. Describes the information involved
    3. Outlines the steps taken
    4. Provides contact information
    5. Meets OAIC requirements`
  }),

  // Cybersecurity
  CRITICAL_INFRASTRUCTURE_BREACH: createObligation({
    id: 'critical_infrastructure_breach',
    title: 'Critical Infrastructure Incident Reporting',
    description: 'Obligation to report cyber security incidents affecting critical infrastructure',
    category: OBLIGATION_CATEGORIES.CYBERSECURITY,
    regulator: REGULATORY_BODIES.ACSC,
    deadlineHours: 12,
    notificationMethod: 'Web Portal',
    requiredActions: [
      'Assess incident severity',
      'Gather technical details',
      'Prepare incident report',
      'Submit to ACSC'
    ],
    applicableIndustries: ['utilities', 'transport', 'healthcare', 'financial_services'],
    legalReference: 'Security of Critical Infrastructure Act 2018',
    aiPrompt: `You are a critical infrastructure incident response assistant. Your task is to help prepare a report for a cyber security incident.
    Consider the following:
    1. The critical infrastructure systems affected
    2. The nature and severity of the incident
    3. The potential impact on essential services
    4. The technical details of the incident
    5. The containment and remediation steps taken
    
    Draft a report that:
    1. Clearly identifies the affected systems
    2. Describes the incident timeline
    3. Outlines the impact assessment
    4. Details the response actions
    5. Meets ACSC reporting requirements`
  }),

  // Workplace Safety
  WORKPLACE_INCIDENT: createObligation({
    id: 'workplace_incident',
    title: 'Workplace Incident Notification',
    description: 'Obligation to notify SafeWork NSW of serious workplace incidents',
    category: OBLIGATION_CATEGORIES.WORKPLACE_SAFETY,
    regulator: REGULATORY_BODIES.SAFEWORK_NSW,
    deadlineHours: 48,
    notificationMethod: 'Phone Call',
    requiredActions: [
      'Secure the incident scene',
      'Provide first aid',
      'Notify SafeWork NSW',
      'Preserve evidence',
      'Prepare incident report'
    ],
    applicableIndustries: ['all'],
    legalReference: 'Work Health and Safety Act 2011 (NSW)',
    aiPrompt: `You are a workplace incident response assistant. Your task is to help prepare a report for a workplace incident.
    Consider the following:
    1. The nature of the incident
    2. The location and time
    3. The people involved
    4. The immediate response taken
    5. The potential causes
    6. The preventive measures needed
    
    Draft a report that:
    1. Clearly describes what happened
    2. Identifies all involved parties
    3. Details the response actions
    4. Suggests preventive measures
    5. Meets SafeWork NSW requirements`
  }),

  // Financial Reporting
  MARKET_DISCLOSURE: createObligation({
    id: 'market_disclosure',
    title: 'Continuous Disclosure Obligation',
    description: 'Obligation to disclose material information to the market',
    category: OBLIGATION_CATEGORIES.FINANCIAL_REPORTING,
    regulator: REGULATORY_BODIES.ASIC,
    deadlineHours: 24,
    notificationMethod: 'ASX Announcement',
    requiredActions: [
      'Assess materiality',
      'Prepare market announcement',
      'Submit to ASX',
      'Update website'
    ],
    applicableIndustries: ['financial_services', 'listed_companies'],
    legalReference: 'Corporations Act 2001 (Cth) - Continuous Disclosure',
    aiPrompt: `You are a market disclosure assistant. Your task is to help prepare a market announcement for material information.
    Consider the following:
    1. The nature of the information
    2. The potential market impact
    3. The timing of the disclosure
    4. The regulatory requirements
    5. The communication strategy
    
    Draft an announcement that:
    1. Clearly states the material information
    2. Explains the potential impact
    3. Provides necessary context
    4. Meets ASX requirements
    5. Maintains market integrity`
  })
};

// Helper function to get obligations by category
export const getObligationsByCategory = (category) => {
  return Object.values(PREDEFINED_OBLIGATIONS).filter(
    obligation => obligation.category === category
  );
};

// Helper function to get obligations by industry
export const getObligationsByIndustry = (industry) => {
  return Object.values(PREDEFINED_OBLIGATIONS).filter(
    obligation => 
      obligation.applicableIndustries.includes('all') || 
      obligation.applicableIndustries.includes(industry)
  );
};

// Helper function to get obligations by regulator
export const getObligationsByRegulator = (regulatorId) => {
  return Object.values(PREDEFINED_OBLIGATIONS).filter(
    obligation => obligation.regulator.id === regulatorId
  );
}; 